import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../services/firebase';
import { collection, doc, setDoc, deleteDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { MdAdd, MdDelete, MdCheckCircle, MdLibraryAddCheck, MdAutoAwesome, MdClose, MdEdit, MdCancel, MdHistory, MdVisibility, MdEditDocument, MdFunctions, MdSchool, MdMenuBook } from 'react-icons/md';
import { generateQuizQuestions } from '../../services/aiService';
import Latex from 'react-latex-next';
import 'katex/dist/katex.min.css';
import VisualMathModal from '../../components/VisualMathModal';
import { 
  ALL_AVAILABLE_SUBJECT_IDS, 
  AVAILABLE_SUBJECTS,
  SUBJECT_NAMES, 
  getSubjectClass, 
  getSubjectName, 
  formatSubject 
} from '../../utils/constants';

const TeacherQuizBuilder = () => {
  const { userProfile } = useAuth();
  const location = useLocation();
  const defaultSub = location.state?.defaultSubject;

  const isAdmin = userProfile?.role === 'admin';
  const subjects = (isAdmin || !userProfile?.assignedSubjects || userProfile.assignedSubjects.length === 0)
    ? ALL_AVAILABLE_SUBJECT_IDS
    : userProfile.assignedSubjects;

  const availableClasses = useMemo(() => {
    if (isAdmin || !userProfile?.assignedSubjects?.length) {
      return ['JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3', 'UTME'];
    }
    const classSet = new Set(userProfile.assignedSubjects.map(s => getSubjectClass(s).toUpperCase()));
    return Array.from(classSet);
  }, [isAdmin, userProfile]);

  const [selectedClass, setSelectedClass] = useState(() => {
    if (defaultSub) return getSubjectClass(defaultSub).toUpperCase();
    return 'SS1';
  });

  const availableSubjectsForClass = useMemo(() => {
    const list = AVAILABLE_SUBJECTS[selectedClass] || [];
    if (isAdmin || !userProfile?.assignedSubjects?.length) {
      return list;
    }
    return list.filter(subId => userProfile.assignedSubjects.includes(subId));
  }, [selectedClass, isAdmin, userProfile]);

  const [selectedSubject, setSelectedSubject] = useState(() => {
    if (defaultSub) return defaultSub;
    const initialList = AVAILABLE_SUBJECTS['SS1'] || [];
    return initialList[0] || 'math_ss1';
  });

  const handleClassChange = (newClass) => {
    setSelectedClass(newClass);
    const newSubjects = AVAILABLE_SUBJECTS[newClass] || [];
    const validSubjects = (isAdmin || !userProfile?.assignedSubjects?.length)
      ? newSubjects
      : newSubjects.filter(subId => userProfile.assignedSubjects.includes(subId));
    if (validSubjects.length > 0 && !validSubjects.includes(selectedSubject)) {
      setSelectedSubject(validSubjects[0]);
    }
  };

  const [title, setTitle] = useState('');
  const [timeLimit, setTimeLimit] = useState('');
  const csvInputRef = useRef(null);

  // Sync if route state passes a default subject
  useEffect(() => {
    if (defaultSub) {
      const cls = getSubjectClass(defaultSub).toUpperCase();
      setSelectedClass(cls);
      setSelectedSubject(defaultSub);
    }
  }, [defaultSub]);

  const [questions, setQuestions] = useState([
    { question: '', options: ['', '', '', '', ''], correctOptionIndex: 0 }
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const [showAiModal, setShowAiModal] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiPdfFile, setAiPdfFile] = useState(null);
  const [aiNumQuestions, setAiNumQuestions] = useState(5);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiError, setAiError] = useState('');

  // LaTeX Preview Toggle
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Visual Math Modal State
  const [mathModalOpen, setMathModalOpen] = useState(false);
  const [mathTarget, setMathTarget] = useState(null);

  // Management tab & edits state
  const [activeTab, setActiveTab] = useState('create'); // 'create' | 'manage'
  const [myQuizzes, setMyQuizzes] = useState([]);
  const [isLoadingQuizzes, setIsLoadingQuizzes] = useState(false);
  const [editingQuizId, setEditingQuizId] = useState(null);

  const fetchMyQuizzes = async () => {
    if (!userProfile) return;
    setIsLoadingQuizzes(true);
    try {
      const q = isAdmin
        ? query(
            collection(db, 'content_approvals'),
            where('type', '==', 'Quiz')
          )
        : query(
            collection(db, 'content_approvals'),
            where('teacherId', '==', userProfile.uid),
            where('type', '==', 'Quiz')
          );
      const snap = await getDocs(q);
      const quizList = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      quizList.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setMyQuizzes(quizList);
    } catch (err) {
      console.error("Error fetching my quizzes:", err);
    } finally {
      setIsLoadingQuizzes(false);
    }
  };

  useEffect(() => {
    fetchMyQuizzes();
  }, [userProfile]);

  const addQuestion = () => {
    setQuestions([...questions, { question: '', options: ['', '', '', '', ''], correctOptionIndex: 0 }]);
  };

  const removeQuestion = (index) => {
    const newQuestions = [...questions];
    newQuestions.splice(index, 1);
    setQuestions(newQuestions);
  };

  const updateQuestion = (index, field, value) => {
    const newQuestions = [...questions];
    newQuestions[index][field] = value;
    setQuestions(newQuestions);
  };

  const updateOption = (qIndex, oIndex, value) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].options[oIndex] = value;
    setQuestions(newQuestions);
  };

  const handlePdfUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'application/pdf') {
        setAiPdfFile(file);
        setAiError('');
      } else {
        setAiPdfFile(null);
        setAiError('Please upload a valid PDF file.');
      }
    }
  };

  const handleGenerateAI = async () => {
    if (!aiText.trim() && !aiPdfFile) {
      setAiError("Please paste some text or upload a PDF to generate questions from.");
      return;
    }

    setIsGenerating(true);
    setAiError('');

    try {
      let base64Pdf = null;
      if (aiPdfFile) {
        base64Pdf = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(aiPdfFile);
          reader.onload = () => {
            const base64String = reader.result.split(',')[1];
            resolve(base64String);
          };
          reader.onerror = error => reject(error);
        });
      }

      const generatedQs = await generateQuizQuestions({
        text: aiText,
        base64Pdf,
        numQuestions: aiNumQuestions
      });

      if (questions.length === 1 && questions[0].question === '') {
        setQuestions(generatedQs);
      } else {
        setQuestions([...questions, ...generatedQs]);
      }

      setShowAiModal(false);
      setAiText('');
      setAiPdfFile(null);
      setSuccessMsg(`Successfully generated ${generatedQs.length} questions with AI!`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setAiError(err.message || "Failed to generate questions. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleBulkUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const rows = text.split('\n');
      const newQuestions = [];

      for (let i = 1; i < rows.length; i++) {
        const row = rows[i].trim();
        if (!row) continue;

        const cols = row.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map(col => col.replace(/^"|"$/g, '').trim());

        if (cols.length >= 6) {
          let q, optA, optB, optC, optD, optE, correctAns;
          if (cols.length >= 7) {
            [q, optA, optB, optC, optD, optE, correctAns] = cols;
          } else {
            [q, optA, optB, optC, optD, correctAns] = cols;
            optE = '';
          }
          let correctIndex = 0;
          const ans = (correctAns || 'A').toUpperCase().trim();
          if (ans === 'B') correctIndex = 1;
          else if (ans === 'C') correctIndex = 2;
          else if (ans === 'D') correctIndex = 3;
          else if (ans === 'E') correctIndex = 4;

          newQuestions.push({
            question: q,
            options: [optA, optB, optC, optD, optE],
            correctOptionIndex: correctIndex
          });
        }
      }

      if (newQuestions.length > 0) {
        if (questions.length === 1 && questions[0].question === '') {
          setQuestions(newQuestions);
        } else {
          setQuestions([...questions, ...newQuestions]);
        }
        setSuccessMsg(`Successfully imported ${newQuestions.length} questions from CSV.`);
      } else {
        setErrorMsg('No valid questions found in CSV.');
      }
      if (csvInputRef.current) csvInputRef.current.value = '';
    };
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const csvContent = "data:text/csv;charset=utf-8," + "Question,Option A,Option B,Option C,Option D,Option E,Correct Answer (A/B/C/D/E)\nWhat is 2+2?,3,4,5,6,,B";
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "CBT_Questions_Template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleInsertMath = (mathString) => {
    if (!mathTarget) return;
    const { qIndex, type, oIndex } = mathTarget;

    if (type === 'question') {
      const currentVal = questions[qIndex].question;
      updateQuestion(qIndex, 'question', (currentVal + ' ' + mathString).trim());
    } else if (type === 'option') {
      const currentVal = questions[qIndex].options[oIndex];
      updateOption(qIndex, oIndex, (currentVal + ' ' + mathString).trim());
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSubject || !title.trim() || !timeLimit.trim()) {
      setErrorMsg("Subject, Quiz Title, and Time Limit are required.");
      return;
    }

    const parsedTime = parseInt(timeLimit);
    if (isNaN(parsedTime) || parsedTime <= 0) {
      setErrorMsg("Time Limit must be a valid number of minutes.");
      return;
    }

    // Validate questions
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.question.trim()) {
        setErrorMsg(`Question ${i + 1} is empty.`);
        return;
      }
      for (let j = 0; j < 5; j++) {
        if (j < 4 && (!q.options[j] || !q.options[j].trim())) {
          setErrorMsg(`Option ${String.fromCharCode(65 + j)} for Question ${i + 1} is empty.`);
          return;
        }
      }
    }

    setIsSubmitting(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const formattedQuestions = questions.map(q => {
        const validOptions = q.options.filter((opt, index) => index < 4 || (index === 4 && opt && opt.trim() !== ''));
        return {
          questionText: q.question.trim(),
          options: validOptions.map(opt => opt.trim()),
          correctAnswerIndex: q.correctOptionIndex
        };
      });

      const quizStatus = isAdmin ? 'approved' : 'pending';

      if (editingQuizId) {
        const docRef = doc(db, 'content_approvals', editingQuizId);
        await setDoc(docRef, {
          title: title.trim(),
          subjectId: selectedSubject,
          subjectName: getSubjectName(selectedSubject),
          classLevel: getSubjectClass(selectedSubject),
          status: quizStatus,
          contentData: {
            timeLimit: parsedTime,
            questions: formattedQuestions
          },
          updatedAt: serverTimestamp(),
        }, { merge: true });

        setSuccessMsg(isAdmin ? "Quiz updated and published successfully!" : "Quiz updated successfully and submitted for admin approval!");
        setEditingQuizId(null);
      } else {
        const docRef = doc(collection(db, 'content_approvals'));
        await setDoc(docRef, {
          id: docRef.id,
          title: title.trim(),
          type: 'Quiz',
          subjectId: selectedSubject,
          subjectName: getSubjectName(selectedSubject),
          classLevel: getSubjectClass(selectedSubject),
          teacherId: userProfile.uid,
          teacherName: userProfile.fullName || (isAdmin ? 'Admin' : 'Teacher'),
          status: quizStatus,
          createdAt: serverTimestamp(),
          contentData: {
            timeLimit: parsedTime,
            questions: formattedQuestions
          },
        });

        setSuccessMsg(isAdmin ? "Quiz published successfully!" : "Quiz submitted successfully for admin approval!");
      }

      setTitle('');
      setTimeLimit('');
      setQuestions([{ question: '', options: ['', '', '', '', ''], correctOptionIndex: 0 }]);
      fetchMyQuizzes();
    } catch (err) {
      console.error(err);
      setErrorMsg("Failed to save quiz. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteQuiz = async (quizId) => {
    if (!window.confirm("Are you sure you want to delete this quiz? This action cannot be undone.")) return;
    try {
      setErrorMsg('');
      setSuccessMsg('');
      await deleteDoc(doc(db, 'content_approvals', quizId));
      setSuccessMsg("Quiz deleted successfully!");
      fetchMyQuizzes();
    } catch (err) {
      console.error(err);
      setErrorMsg("Failed to delete quiz.");
    }
  };

  const handleEditQuiz = (quiz) => {
    setEditingQuizId(quiz.id);
    setSelectedSubject(quiz.subjectId);
    if (quiz.subjectId) {
      setSelectedClass(getSubjectClass(quiz.subjectId).toUpperCase());
    }
    setTitle(quiz.title);
    setTimeLimit(quiz.contentData?.timeLimit?.toString() || '');

    const mappedQuestions = (quiz.contentData?.questions || []).map(q => {
      const opts = q.options || ['', '', '', ''];
      if (opts.length === 4) opts.push('');
      return {
        question: q.questionText || '',
        options: opts,
        correctOptionIndex: q.correctAnswerIndex ?? 0
      };
    });

    setQuestions(mappedQuestions.length > 0 ? mappedQuestions : [{ question: '', options: ['', '', '', '', ''], correctOptionIndex: 0 }]);
    setActiveTab('create');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingQuizId(null);
    setTitle('');
    setTimeLimit('');
    setQuestions([{ question: '', options: ['', '', '', '', ''], correctOptionIndex: 0 }]);
  };

  return (
    <div className="max-w-4xl mx-auto w-full">
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-darkNavy mb-2 font-poppins">Quiz Builder & Manager</h1>
          <p className="text-sm md:text-base text-gray-500">Create CBT quizzes or manage existing assessments.</p>
        </div>
        <div className="flex gap-2">
          <input
            type="file"
            ref={csvInputRef}
            onChange={handleBulkUpload}
            accept=".csv"
            className="hidden"
          />
          {activeTab === 'create' && (
            <button
              type="button"
              onClick={downloadTemplate}
              className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 font-bold rounded-xl shadow-sm hover:bg-gray-50 transition-all text-sm"
            >
              Download Template
            </button>
          )}
          {activeTab === 'create' && (
            <button
              type="button"
              onClick={() => csvInputRef.current?.click()}
              className="px-5 py-2.5 bg-green-600 text-white font-bold rounded-xl shadow-md hover:bg-green-700 hover:shadow-lg transition-all flex items-center justify-center gap-2"
            >
              Upload CSV Quiz
            </button>
          )}
          {activeTab === 'create' && (
            <button
              type="button"
              onClick={() => setShowAiModal(true)}
              className="px-5 py-2.5 bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-bold rounded-xl shadow-md hover:shadow-lg hover:from-purple-600 hover:to-indigo-600 transition-all flex items-center justify-center gap-2"
            >
              <MdAutoAwesome size={20} /> Generate with AI
            </button>
          )}
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex border-b border-gray-200 mb-8">
        <button
          onClick={() => {
            setActiveTab('create');
            setErrorMsg('');
            setSuccessMsg('');
          }}
          className={`py-3 px-6 font-bold font-poppins text-sm border-b-2 transition-all ${activeTab === 'create'
            ? 'border-primary text-primary'
            : 'border-transparent text-gray-500 hover:text-darkNavy'
            }`}
        >
          {editingQuizId ? 'Edit Quiz' : 'Build Quiz'}
        </button>
        <button
          onClick={() => {
            setActiveTab('manage');
            setErrorMsg('');
            setSuccessMsg('');
            fetchMyQuizzes();
          }}
          className={`py-3 px-6 font-bold font-poppins text-sm border-b-2 transition-all ${activeTab === 'manage'
            ? 'border-primary text-primary'
            : 'border-transparent text-gray-500 hover:text-darkNavy'
            }`}
        >
          Manage Quizzes
        </button>
      </div>

      {successMsg && (
        <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-xl flex items-center gap-3 border border-green-200">
          <MdCheckCircle size={24} />
          <span className="font-semibold">{successMsg}</span>
        </div>
      )}

      {errorMsg && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-xl border border-red-200">
          <span className="font-semibold">{errorMsg}</span>
        </div>
      )}

      {activeTab === 'create' ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          {editingQuizId && (
            <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-800 p-4 rounded-xl flex justify-between items-center">
              <span className="text-sm font-semibold">Editing Quiz: <strong className="text-blue-900">{title}</strong> (Re-submitting resets status to pending)</span>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="text-sm text-red-600 hover:text-red-800 font-bold flex items-center gap-1"
              >
                <MdCancel size={16} /> Cancel Edit
              </button>
            </div>
          )}

          <div className="flex justify-end mb-4">
            <button
              type="button"
              onClick={() => setIsPreviewMode(!isPreviewMode)}
              className={`px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all ${isPreviewMode
                ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {isPreviewMode ? <MdEditDocument size={18} /> : <MdVisibility size={18} />}
              {isPreviewMode ? 'Edit Mode' : 'Preview Math (LaTeX)'}
            </button>
          </div>

          <div className="bg-white p-4 sm:p-6 md:p-8 rounded-2xl border border-gray-200 shadow-sm mb-6">
            {/* Cascading Class and Subject Selectors */}
            <div className="p-4 bg-gradient-to-r from-blue-50/70 to-indigo-50/50 rounded-xl border border-blue-100 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 1. Class Level */}
                <div>
                  <label className="flex items-center gap-1.5 text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                    <MdSchool className="text-primary text-base" /> 1. Class Level
                  </label>
                  <select
                    value={selectedClass}
                    onChange={(e) => handleClassChange(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl font-semibold text-darkNavy shadow-sm focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all cursor-pointer"
                    required
                  >
                    {availableClasses.map(cls => (
                      <option key={cls} value={cls}>{cls}</option>
                    ))}
                  </select>
                </div>

                {/* 2. Subject */}
                <div>
                  <label className="flex items-center gap-1.5 text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                    <MdMenuBook className="text-primary text-base" /> 2. Subject
                  </label>
                  <select
                    value={selectedSubject}
                    onChange={(e) => setSelectedSubject(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl font-semibold text-darkNavy shadow-sm focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all cursor-pointer"
                    required
                  >
                    {availableSubjectsForClass.length === 0 && <option value="">No subjects in {selectedClass}</option>}
                    {availableSubjectsForClass.map(sub => (
                      <option key={sub} value={sub}>{getSubjectName(sub)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Quiz Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. End of Term CBT Examination"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all shadow-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Exam Time Limit (Minutes)</label>
                <input
                  type="number"
                  value={timeLimit}
                  onChange={(e) => setTimeLimit(e.target.value)}
                  placeholder="e.g. 45"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all shadow-sm"
                  required
                />
              </div>
            </div>
          </div>

          {questions.map((q, qIndex) => (
            <div key={qIndex} className="bg-white p-4 sm:p-6 md:p-8 rounded-2xl border border-gray-200 shadow-sm">
              <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-4">
                <h3 className="font-bold text-lg text-darkNavy">Question {qIndex + 1}</h3>
                {questions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeQuestion(qIndex)}
                    className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <MdDelete size={22} />
                  </button>
                )}
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-700">Question Text</label>
                  {!isPreviewMode && (
                    <button
                      type="button"
                      onClick={() => {
                        setMathTarget({ qIndex, type: 'question' });
                        setMathModalOpen(true);
                      }}
                      className="text-xs font-bold bg-indigo-50 text-indigo-600 px-3 py-1.5 rounded-lg hover:bg-indigo-100 flex items-center gap-1 transition-colors border border-indigo-200"
                    >
                      <MdFunctions size={16} /> Insert Math
                    </button>
                  )}
                </div>
                {isPreviewMode ? (
                  <div className="w-full px-4 py-3 border border-indigo-200 bg-indigo-50/30 rounded-xl min-h-[80px] text-gray-800">
                    <Latex>{q.question || '...'}</Latex>
                  </div>
                ) : (
                  <div>
                    <textarea
                      value={q.question}
                      onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                      placeholder="Type the question here... Use $x^2$ for inline math, and $$x^2$$ for block math."
                      rows={2}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                      required
                    ></textarea>
                    {q.question && q.question.includes('$') && (
                      <div className="mt-2 px-4 py-2.5 bg-indigo-50 border border-indigo-200 rounded-xl text-sm text-gray-800">
                        <span className="text-xs font-bold text-indigo-400 uppercase tracking-wider block mb-1">Math Preview:</span>
                        <Latex>{q.question}</Latex>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {q.options.map((opt, oIndex) => (
                  <div key={oIndex} className="flex items-center gap-3">
                    <input
                      type="radio"
                      name={`correct-${qIndex}`}
                      checked={q.correctOptionIndex === oIndex}
                      onChange={() => updateQuestion(qIndex, 'correctOptionIndex', oIndex)}
                      className="w-5 h-5 text-primary border-gray-300 focus:ring-primary"
                    />
                    {isPreviewMode ? (
                      <div className={`w-full px-4 py-2.5 border rounded-xl overflow-x-auto ${q.correctOptionIndex === oIndex
                        ? 'border-green-400 bg-green-50'
                        : 'border-indigo-200 bg-indigo-50/30'
                        }`}>
                        <Latex>{opt || '...'}</Latex>
                      </div>
                    ) : (
                      <div className="relative w-full">
                        <input
                          type="text"
                          value={opt}
                          onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                          placeholder={`Option ${String.fromCharCode(65 + oIndex)}`}
                          className={`w-full px-4 py-2.5 pr-12 border rounded-xl focus:outline-none transition-all ${q.correctOptionIndex === oIndex
                            ? 'border-green-400 bg-green-50 ring-2 ring-green-100'
                            : 'border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/50'
                            }`}
                          required={oIndex < 4}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setMathTarget({ qIndex, type: 'option', oIndex });
                            setMathModalOpen(true);
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 p-1.5 rounded-lg border border-indigo-200 transition-colors"
                          title="Insert Math"
                        >
                          <MdFunctions size={16} />
                        </button>
                        {opt && opt.includes('$') && (
                          <div className="mt-1.5 px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-gray-800">
                            <Latex>{opt}</Latex>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 italic">Select the radio button next to the correct answer.</p>
            </div>
          ))}

          <div className="flex gap-4">
            <button
              type="button"
              onClick={addQuestion}
              className="flex-1 py-4 bg-green-600 text-white font-bold rounded-xl shadow-md hover:bg-green-700 hover:shadow-lg transition-all flex items-center justify-center gap-2"
            >
              <MdAdd size={20} /> Add Question
            </button>

            <button
              type="submit"
              disabled={isSubmitting || subjects.length === 0}
              className="flex-1 py-4 bg-primary text-white font-bold rounded-xl shadow-md hover:bg-blue-700 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <MdLibraryAddCheck size={20} /> {editingQuizId ? 'Update Quiz' : 'Submit Quiz'}
                </>
              )}
            </button>
          </div>
        </form>
      ) : (
        <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-200 shadow-sm animate-fadeIn">
          <h2 className="text-xl font-bold text-darkNavy mb-4 font-poppins flex items-center gap-2">
            <MdHistory className="text-primary" /> My Quizzes ({myQuizzes.length})
          </h2>

          {isLoadingQuizzes ? (
            <div className="text-center py-8 text-gray-500 font-medium">Loading quizzes...</div>
          ) : myQuizzes.length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl border border-dashed">
              You have not created any quizzes yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 text-gray-400 text-sm font-bold">
                    <th className="pb-3 pr-4">Quiz Title</th>
                    <th className="pb-3 px-4">Subject</th>
                    <th className="pb-3 px-4">Questions</th>
                    <th className="pb-3 px-4">Status</th>
                    <th className="pb-3 pl-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {myQuizzes.map((quiz) => {
                    const qCount = quiz.contentData?.questions?.length || 0;
                    return (
                      <tr key={quiz.id} className="text-darkNavy text-sm hover:bg-gray-50/50 transition-colors">
                        <td className="py-4 pr-4 font-bold max-w-[200px] truncate">{quiz.title}</td>
                        <td className="py-4 px-4 text-gray-500">{getSubjectName(quiz.subjectId)} ({getSubjectClass(quiz.subjectId).toUpperCase()})</td>
                        <td className="py-4 px-4 text-gray-600 font-semibold">{qCount} {qCount === 1 ? 'Question' : 'Questions'}</td>
                        <td className="py-4 px-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${quiz.status === 'approved'
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                            }`}>
                            {quiz.status}
                          </span>
                        </td>
                        <td className="py-4 pl-4 text-right flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEditQuiz(quiz)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit Quiz"
                          >
                            <MdEdit size={20} />
                          </button>
                          <button
                            onClick={() => handleDeleteQuiz(quiz.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Quiz"
                          >
                            <MdDelete size={20} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* AI Generator Modal */}
      {showAiModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <div className="flex items-center gap-2 text-indigo-600">
                <MdAutoAwesome size={24} />
                <h2 className="text-xl font-bold font-poppins text-darkNavy">AI Quiz Generator</h2>
              </div>
              <button
                onClick={() => setShowAiModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <MdClose size={24} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {aiError && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100">
                  {aiError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Upload Document (PDF)</label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handlePdfUpload}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500/50 transition-all text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                  />
                  {aiPdfFile && <p className="text-xs text-green-600 mt-2 font-semibold flex items-center gap-1"><MdCheckCircle size={14} /> Attached: {aiPdfFile.name}</p>}
                </div>

                <div className="flex items-center justify-center">
                  <span className="h-px bg-gray-200 flex-1"></span>
                  <span className="px-4 text-sm text-gray-400 font-bold uppercase">OR</span>
                  <span className="h-px bg-gray-200 flex-1"></span>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Source Text</label>
                  <textarea
                    value={aiText}
                    onChange={(e) => setAiText(e.target.value)}
                    placeholder="Paste your lesson content, article, or notes here. The AI will read this and generate multiple-choice questions."
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all text-sm"
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Number of Questions</label>
                  <select
                    value={aiNumQuestions}
                    onChange={(e) => setAiNumQuestions(parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all bg-white"
                  >
                    <option value={3}>3 Questions</option>
                    <option value={5}>5 Questions</option>
                    <option value={10}>10 Questions</option>
                    <option value={15}>15 Questions</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowAiModal(false)}
                className="px-6 py-2.5 rounded-xl font-bold text-gray-600 hover:bg-gray-200 transition-colors"
                disabled={isGenerating}
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateAI}
                disabled={isGenerating || (!aiText.trim() && !aiPdfFile)}
                className="px-6 py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 transition-all shadow-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <MdAutoAwesome size={20} /> Generate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visual Math Modal */}
      <VisualMathModal
        isOpen={mathModalOpen}
        onClose={() => setMathModalOpen(false)}
        onInsert={handleInsertMath}
      />
    </div>
  );
};

export default TeacherQuizBuilder;
